import React, { useState, useEffect, useMemo } from 'react';
import {
    Container, Typography, Box, Paper, Grid, TextField, Button,
    Card, CardContent, CircularProgress, Chip, Stack, Alert,
    Autocomplete, Switch, Dialog,
    DialogTitle, DialogContent, DialogActions, Avatar
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { useAuth } from '../context/AuthContext';
import { Currency } from '../constants/enums';
import { useTranslation } from 'react-i18next';

const arborGreen = '#006D43';

const AdminDemoSeed = () => {
    const { t } = useTranslation();
    const { currentUser, api } = useAuth();

    // User Search State
    const [userOptions, setUserOptions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [usersLoading, setUsersLoading] = useState(false);

    // Ledger Config State
    const [ledgerName, setLedgerName] = useState('');
    const [selectedCurrencies, setSelectedCurrencies] = useState([Currency.CAD, Currency.USD]);

    // Modules State
    const [seedTransactions, setSeedTransactions] = useState(true);
    const [seedCashflow, setSeedCashflow] = useState(true);
    const [seedBudget, setSeedBudget] = useState(true);
    const [seedInvestments, setSeedInvestments] = useState(true);

    // Submission & Feedback State
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [resultSummary, setResultSummary] = useState(null);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);

    // Debounced User Search
    useEffect(() => {
        let isMounted = true;
        const timer = setTimeout(async () => {
            if (!currentUser?.is_super_admin) return;
            setUsersLoading(true);
            try {
                const response = await api.get('/admin/demo/users', {
                    params: { query: searchQuery.trim(), limit: 20 }
                });
                if (isMounted) {
                    setUserOptions(response.data || []);
                }
            } catch (err) {
                console.error("Failed to search users:", err);
            } finally {
                if (isMounted) setUsersLoading(false);
            }
        }, 300);

        return () => {
            isMounted = false;
            clearTimeout(timer);
        };
    }, [searchQuery, api, currentUser]);

    // Auto-update Ledger Name when User or Currencies change if user hasn't typed custom name
    useEffect(() => {
        if (selectedUser) {
            const userName = selectedUser.first_name
                ? `${selectedUser.first_name}'s`
                : `${selectedUser.username}'s`;
            const currStr = selectedCurrencies.join(' & ');
            setLedgerName(`${userName} Demo Portfolio (${currStr})`);
        } else {
            setLedgerName('');
        }
    }, [selectedUser, selectedCurrencies]);

    const handleToggleCurrency = (currency) => {
        if (selectedCurrencies.includes(currency)) {
            if (selectedCurrencies.length === 1) {
                setErrorMessage("At least one currency must remain selected.");
                return;
            }
            setSelectedCurrencies(selectedCurrencies.filter(c => c !== currency));
        } else {
            setSelectedCurrencies([...selectedCurrencies, currency]);
        }
        setErrorMessage('');
    };

    const isFormValid = useMemo(() => {
        return (
            selectedUser &&
            ledgerName.trim().length > 0 &&
            selectedCurrencies.length > 0 &&
            (seedTransactions || seedCashflow || seedBudget || seedInvestments)
        );
    }, [selectedUser, ledgerName, selectedCurrencies, seedTransactions, seedCashflow, seedBudget, seedInvestments]);

    const handleGenerateDemo = async () => {
        if (!isFormValid) return;
        setSubmitting(true);
        setErrorMessage('');

        try {
            const payload = {
                user_id: selectedUser.id,
                ledger_name: ledgerName.trim(),
                currencies: selectedCurrencies,
                seed_transactions: seedTransactions,
                seed_cashflow: seedCashflow,
                seed_budget: seedBudget,
                seed_investments: seedInvestments
            };

            const res = await api.post('/admin/demo/seed', payload);
            setResultSummary(res.data);
            setSuccessDialogOpen(true);
        } catch (err) {
            console.error("Failed to generate demo data:", err);
            const detail = err.response?.data?.detail;
            setErrorMessage(
                typeof detail === 'string'
                    ? detail
                    : 'Failed to generate demo data. Please try again.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = () => {
        setSelectedUser(null);
        setLedgerName('');
        setSelectedCurrencies([Currency.CAD, Currency.USD]);
        setSeedTransactions(true);
        setSeedCashflow(true);
        setSeedBudget(true);
        setSeedInvestments(true);
        setErrorMessage('');
        setResultSummary(null);
        setSuccessDialogOpen(false);
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
            {/* Header Banner */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 3, sm: 4 },
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #004D2F 0%, #006D43 100%)',
                    color: '#ffffff',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 38, color: '#A3F3C6' }} />
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', color: '#ffffff' }}>
                        Demo Data Generator
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', maxWidth: 750 }}>
                    Quickly spin up a brand new, fully populated demo ledger for any target user with historical transactions, weekly cashflow forecast, annual budget hierarchy, and real famous stocks & ETFs with calculated ACB gains.
                </Typography>
            </Paper>

            {errorMessage && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setErrorMessage('')}>
                    {errorMessage}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Left Column: Configuration */}
                <Grid item xs={12} md={7}>
                    {/* Step 1: Select User */}
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Avatar sx={{ bgcolor: arborGreen, width: 28, height: 28, fontSize: '0.85rem', fontWeight: 700 }}>1</Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Select Target User
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Search by name, email, or username. The generated demo ledger will belong to this user.
                        </Typography>

                        <Autocomplete
                            options={userOptions}
                            getOptionLabel={(opt) => `${opt.first_name || ''} ${opt.last_name || ''} (${opt.email})`.trim()}
                            loading={usersLoading}
                            value={selectedUser}
                            onChange={(e, val) => setSelectedUser(val)}
                            onInputChange={(e, val) => setSearchQuery(val)}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Type user name or email..."
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <PersonSearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        endAdornment: (
                                            <>
                                                {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: '0.85rem', fontWeight: 700 }}>
                                            {(option.first_name || option.username || 'U').charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {option.first_name || option.last_name ? `${option.first_name} ${option.last_name}` : option.username}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.email}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Chip label={t(option.service_tier) || option.service_tier || t('FREE')} size="small" variant="outlined" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                                </Box>
                            )}
                        />

                        {selectedUser && (
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'surface-container-lowest', borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar sx={{ width: 36, height: 36, bgcolor: arborGreen, fontWeight: 700 }}>
                                        {(selectedUser.first_name || selectedUser.username).charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            {selectedUser.first_name ? `${selectedUser.first_name} ${selectedUser.last_name}` : selectedUser.username}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {selectedUser.email} (ID: {selectedUser.id})
                                        </Typography>
                                    </Box>
                                </Box>
                                <Chip label="Selected" color="success" size="small" sx={{ fontWeight: 700 }} />
                            </Box>
                        )}
                    </Paper>

                    {/* Step 2: Ledger & Currencies */}
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Avatar sx={{ bgcolor: arborGreen, width: 28, height: 28, fontSize: '0.85rem', fontWeight: 700 }}>2</Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Ledger & Currencies
                            </Typography>
                        </Box>

                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            New Ledger Name
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={ledgerName}
                            onChange={(e) => setLedgerName(e.target.value)}
                            placeholder="e.g. Demo Portfolio & Books"
                            sx={{ mb: 3 }}
                        />

                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            Seeded Currencies
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Select one or both currencies. Dedicated accounts and transactions will be created for each selected currency.
                        </Typography>

                        <Stack direction="row" spacing={1.5}>
                            {[
                                { key: Currency.CAD, label: 'CAD — Canadian Dollar', flag: '🇨🇦' },
                                { key: Currency.USD, label: 'USD — US Dollar', flag: '🇺🇸' },
                            ].map((curr) => {
                                const selected = selectedCurrencies.includes(curr.key);
                                return (
                                    <Chip
                                        key={curr.key}
                                        label={`${curr.flag} ${curr.label}`}
                                        clickable
                                        onClick={() => handleToggleCurrency(curr.key)}
                                        color={selected ? 'primary' : 'default'}
                                        variant={selected ? 'filled' : 'outlined'}
                                        sx={{
                                            py: 2.2,
                                            px: 1,
                                            fontWeight: 700,
                                            borderRadius: 2,
                                            bgcolor: selected ? arborGreen : 'transparent',
                                            '&:hover': {
                                                bgcolor: selected ? '#005836' : 'rgba(0, 109, 67, 0.08)'
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                    </Paper>
                </Grid>

                {/* Right Column: Module Toggles & Action */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <Avatar sx={{ bgcolor: arborGreen, width: 28, height: 28, fontSize: '0.85rem', fontWeight: 700 }}>3</Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    Data Modules to Seed
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                                Toggle the functional domains to include in this demo books.
                            </Typography>

                            <Stack spacing={2}>
                                {/* Module 1: Transactions */}
                                <Card variant="outlined" sx={{ borderRadius: 2, borderColor: seedTransactions ? arborGreen : 'divider' }}>
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <ReceiptLongIcon sx={{ color: arborGreen }} />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                        Transactions & Entries
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        6–12 months of salary, groceries, utilities, dining
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Switch
                                                checked={seedTransactions}
                                                onChange={(e) => setSeedTransactions(e.target.checked)}
                                                color="primary"
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>

                                {/* Module 2: Cashflow */}
                                <Card variant="outlined" sx={{ borderRadius: 2, borderColor: seedCashflow ? arborGreen : 'divider' }}>
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <ShowChartIcon sx={{ color: arborGreen }} />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                        Cashflow & Scheduled Payments
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Checking/Savings balances, payroll & recurring bills
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Switch
                                                checked={seedCashflow}
                                                onChange={(e) => setSeedCashflow(e.target.checked)}
                                                color="primary"
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>

                                {/* Module 3: Budget */}
                                <Card variant="outlined" sx={{ borderRadius: 2, borderColor: seedBudget ? arborGreen : 'divider' }}>
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <PieChartIcon sx={{ color: arborGreen }} />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                        Annual Budget Plan
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Income goal & multi-level category budget items
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Switch
                                                checked={seedBudget}
                                                onChange={(e) => setSeedBudget(e.target.checked)}
                                                color="primary"
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>

                                {/* Module 4: Investments */}
                                <Card variant="outlined" sx={{ borderRadius: 2, borderColor: seedInvestments ? arborGreen : 'divider' }}>
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <AccountBalanceWalletIcon sx={{ color: arborGreen }} />
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                        Investments & Famous Stocks
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        TFSA/RRSP/401k, VOO, VFV.TO, AAPL, ACB gains
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Switch
                                                checked={seedInvestments}
                                                onChange={(e) => setSeedInvestments(e.target.checked)}
                                                color="primary"
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Stack>
                        </Box>

                        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                disabled={!isFormValid || submitting}
                                onClick={handleGenerateDemo}
                                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                                sx={{
                                    py: 1.5,
                                    fontWeight: 800,
                                    fontSize: '1rem',
                                    borderRadius: 2,
                                    bgcolor: arborGreen,
                                    '&:hover': { bgcolor: '#005836' }
                                }}
                            >
                                {submitting ? 'Generating Demo Data...' : 'Generate Demo Ledger'}
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Success Dialog */}
            <Dialog
                open={successDialogOpen}
                onClose={() => setSuccessDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32 }} />
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            {t("Demo Ledger Created!")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t("Successfully generated with rich demo data")}
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {resultSummary && (
                        <Box sx={{ mt: 1 }}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'surface-container-lowest', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: arborGreen }}>
                                    {resultSummary.ledger_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("Owner:")} <strong>{resultSummary.user_email}</strong> ({t("Ledger ID:")} {resultSummary.ledger_id})
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("Currencies:")} {resultSummary.currencies?.join(', ')}
                                </Typography>
                            </Paper>

                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                                {t("Seeded Data Breakdown")}
                            </Typography>

                            <Grid container spacing={1.5}>
                                <Grid item xs={6}>
                                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">{t("Transactions")}</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{resultSummary.transactions_count}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">{t("Accounts")}</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{resultSummary.accounts_count}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">{t("Scheduled Payments")}</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{resultSummary.scheduled_payments_count}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">{t("Budget Items")}</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{resultSummary.budget_items_count}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">{t("Investment Accounts")}</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{resultSummary.investment_accounts_count}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">{t("Stock/ETF Trades")}</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{resultSummary.investment_transactions_count}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={handleReset}
                        startIcon={<RefreshIcon />}
                        sx={{ fontWeight: 700 }}
                    >
                        {t("Seed Another")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setSuccessDialogOpen(false)}
                        sx={{
                            fontWeight: 800,
                            bgcolor: arborGreen,
                            '&:hover': { bgcolor: '#005836' }
                        }}
                    >
                        {t("Done")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminDemoSeed;
